import { message, Modal } from "antd";
import React from "react";
import { useForm, Controller } from "react-hook-form";
import Icon from "../../../assets/icons";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import useRequest from "../../../components/hooks/use-request";
import { FaSpinner } from "react-icons/fa";

const initialValues = {
  amount: 0,
  category: "",
  reason: "",
};

const categories = [
  { label: "Platform Penalty", value: "platform-penalty" },
  { label: "Error Correction", value: "error-correction" },
  { label: "Fee Adjustment", value: "fee-adjustment" },
  { label: "Chargeback Recovery", value: "chargeback-recovery" },
  { label: "Other", value: "other" },
];

const DeductionModal = ({ open, onCancel, account, refetchAccount }: any) => {
  const userToken = localStorage.getItem("token");

  const { makeRequest, loading } = useRequest(
    `/users/${account?.id}/deduct`,
    "POST",
    {
      Authorization: `Bearer ${userToken}`,
    }
  );

  const schema = z.object({
    amount: z
      .number()
      .min(1, { message: "Amount is required" })
      .refine((val) => Number(val) <= account?.balance, {
        message: "Amount cannot be greater than wallet balance",
      }),
    category: z.string().min(1, { message: "Category is required" }),
    reason: z.string().min(1, { message: "Reason is required" }),
  });

  const {
    handleSubmit,
    formState: { errors },
    control,
    formState: { isValid },
  } = useForm({
    mode: "onChange",
    defaultValues: initialValues,
    resolver: zodResolver(schema),
  });

  const deductBalance = async (data: any) => {
    const [response] = await makeRequest(data);
    if (response?.status === true) {
      message.success(response?.message || "Balance deducted successfully");
      refetchAccount();
      onCancel();
    } else {
      message.error(response?.message);
    }
  };

  const onSubmit = (data: any) => {
    deductBalance(data);
  };

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      centered
      width={470}
      footer={null}
      className="!p-0"
    >
      <div className="flex flex-col gap-4 !p-0">
        <div className="flex justify-between">
          <p className="text-sm font-semibold">Deduct wallet balance</p>
          <button onClick={onCancel}>
            <Icon name="cancel" />
          </button>
        </div>
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
          <Controller
            control={control}
            name="amount"
            render={({ field }) => (
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-1">
                  <label htmlFor="amount">Amount to deduct</label>
                  {/* show error message if amount is greater than account balance */}

                  <div className="border border-[#D0D5DD] rounded-lg p-2 flex items-center gap-2">
                    <p className="text-sm font-medium">₦</p>

                    <input
                      type="text"
                      id="amount"
                      {...field}
                      onChange={(e) => {
                        const value = e.target.value;
                        const formattedValue = value.replace(/[^0-9]/g, "");
                        field.onChange(Number(formattedValue));
                      }}
                      className="w-full outline-none"
                    />
                  </div>
                  {errors.amount && (
                    <p className="text-xs text-red-500">
                      {errors.amount?.message}
                    </p>
                  )}
                  <p className="text-xs font-medium">
                    Wallet Balance: ₦{account?.balance}
                  </p>
                </div>
              </div>
            )}
          />
          <Controller
            control={control}
            name="category"
            render={({ field }) => (
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-2">
                  <label htmlFor="category">
                    Why are you deducting this amount?
                  </label>
                  <select
                    id="category"
                    {...field}
                    className="border border-[#D0D5DD] rounded-lg p-2"
                  >
                    {categories.map((category) => (
                      <option key={category.value} value={category.value}>
                        {category.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          />
          <Controller
            control={control}
            name="reason"
            render={({ field }) => (
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-2">
                  <label htmlFor="reason">Enter a detailed reason</label>
                  <textarea
                    id="reason"
                    {...field}
                    className="border border-[#D0D5DD] rounded-lg p-2 resize-none h-24"
                  />
                </div>
              </div>
            )}
          />

          <button
            type="submit"
            className="bg-[#0979A1] text-[#fff] px-4 py-2 rounded-lg text-sm font-medium w-full disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            disabled={!isValid || loading}
          >
            {loading ? (
              <FaSpinner className="animate-spin" />
            ) : (
              <p className="text-sm font-medium">Deduct</p>
            )}
          </button>
        </form>
      </div>
    </Modal>
  );
};

export default DeductionModal;
